import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Star, 
  User,
  Calendar,
  MessageSquare,
  Save,
  CheckCircle
} from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS, ADMIN_ROUTES } from '@constants/admin.constants';
import feedbackAdminService from '@services/admin/feedback.admin.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const FeedbackDetails = () => {
  const { feedbackId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');
  const [isReviewed, setIsReviewed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_FEEDBACK)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchFeedbackDetails();
  }, [feedbackId]);

  const fetchFeedbackDetails = async () => {
    setLoading(true);
    try {
      const response = await feedbackAdminService.getFeedbackById(feedbackId);
      if (response.success) {
        setFeedback(response?.data?.data);
        setAdminNotes(response?.data?.data.adminNotes || '');
        setIsReviewed(response?.data?.data.isReviewed || false);
      }
    } catch (error) {
      toast.error('Failed to load feedback details');
      navigate(ADMIN_ROUTES.FEEDBACK);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await feedbackAdminService.updateReview(feedbackId, {
        adminNotes,
        isReviewed
      });

      if (response.success) {
        toast.success('Feedback updated successfully');
        fetchFeedbackDetails();
      }
    } catch (error) {
      toast.error('Failed to update feedback');
    } finally {
      setSaving(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-6 h-6 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading || !feedback) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_FEEDBACK}>
      <div className="min-h-full bg-gray-50 p-6">
          <div className="mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ADMIN_ROUTES.FEEDBACK)}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Feedback
            </Button>
            <h1 className="text-3xl font-bold text-primary-900 mb-2">
              Feedback Details
            </h1>
          </div>

          <Card className="p-6 mb-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {feedback.user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-primary-900">
                    {feedback.user?.email || 'Unknown User'}
                  </h2>
                  <div className="flex items-center gap-2 mt-2">
                    {renderStars(feedback.rating)}
                    <span className="text-sm text-primary-600 ml-2">
                      ({feedback.rating}/5)
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-sm text-primary-600 mb-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(feedback.created_at).toLocaleString()}
                </div>
                {feedback.isReviewed ? (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    Reviewed
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                    Pending Review
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-primary-600 mb-1">Feedback Type</p>
                <p className="text-base font-medium text-primary-900 capitalize">
                  {feedback.feedbackType?.replace('_', ' ')}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-primary-700 mb-2">
                  Feedback Message
                </label>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-primary-900 whitespace-pre-wrap">
                    {feedback.feedbackMessage || 'No message provided'}
                  </p>
                </div>
              </div>

              {feedback.improvementSuggestions && (
                <div>
                  <label className="block text-sm font-semibold text-primary-700 mb-2">
                    Improvement Suggestions
                  </label>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-primary-900 whitespace-pre-wrap">
                      {feedback.improvementSuggestions}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-bold text-primary-900 mb-4">
              Admin Review
            </h3>

            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    checked={isReviewed}
                    onChange={(e) => setIsReviewed(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-primary-900">
                    Mark as Reviewed
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary-700 mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this feedback..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                />
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => navigate(ADMIN_ROUTES.FEEDBACK)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                  loading={saving}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>
      </div>
    </PermissionGuard>
  );
};

export default FeedbackDetails;
